import React, { useState } from 'react';
import { Link, usePage } from '@inertiajs/react';
import { NAV_LINKS } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import { Sun, Moon, Menu, X } from 'lucide-react';
import logo from '../../assets/images/logo.jpeg';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const { url } = usePage();

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  return (
    <header className='bg-background/80 backdrop-blur-md sticky top-0 z-50 border-b border-border'>
      <div className='container mx-auto px-6 py-4 flex justify-between items-center'>
        {/* Left: Logo */}
        <Link
          href='/'
          className='shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md'
          aria-label='Digital Wave Africa Home'
        >
          <img src={logo} alt='Digital Wave Africa' className='h-10 w-auto' />
        </Link>

        {/* Center: Navigation (Desktop) */}
        <nav className='hidden lg:flex items-center justify-center space-x-6 flex-1 px-4' aria-label='Main navigation'>
          {NAV_LINKS.map((link) => (
            <Link
              key={link.name}
              href={link.path}
              aria-current={url === link.path ? 'page' : undefined}
              className={`whitespace-nowrap text-md font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md px-2 py-1 ${
                url === link.path ? 'text-primary' : 'text-muted-foreground hover:text-primary'
              }`}
            >
              {link.name}
            </Link>
          ))}
        </nav>

        {/* Right: Actions (Theme Toggle + CTA + Mobile Menu) */}
        <div className='flex items-center space-x-4 shrink-0'>
          {/* Theme Toggle Button */}
          <button
            onClick={toggleTheme}
            className='p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-accent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring'
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? <Sun className='h-5 w-5' /> : <Moon className='h-5 w-5' />}
          </button>

          {/* Desktop CTA */}
          <Link
            href='/community'
            className='hidden lg:block whitespace-nowrap bg-primary text-primary-foreground font-semibold px-5 py-2 rounded-md hover:bg-primary/90 transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
          >
            Join Community
          </Link>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className='lg:hidden p-2 text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md'
            aria-label={isMenuOpen ? 'Close main menu' : 'Open main menu'}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X className='w-6 h-6' /> : <Menu className='w-6 h-6' />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {isMenuOpen && (
        <div className='lg:hidden bg-background pb-4 border-t border-border'>
          <nav className='flex flex-col items-center space-y-4 pt-4' aria-label='Mobile navigation'>
            {NAV_LINKS.map((link) => (
              <Link
                key={link.name}
                href={link.path}
                onClick={() => setIsMenuOpen(false)}
                aria-current={url === link.path ? 'page' : undefined}
                className={`text-muted-foreground hover:text-primary transition-colors text-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md px-2 ${
                  url === link.path ? 'text-primary font-semibold' : ''
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link
              href='/community'
              onClick={() => setIsMenuOpen(false)}
              className='bg-primary w-1/2 text-primary-foreground font-semibold px-5 py-3 rounded-full hover:bg-primary/90 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
            >
              Join Community
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
